import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { KeyRound, LogOut, Menu, UserRound } from 'lucide-react';
import { ROLE_LABEL_KEY } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/auth-provider';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { ConfirmDialog } from '@/shared/ui/confirm-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { FormField } from '@/shared/ui/form-field';
import { Input } from '@/shared/ui/input';
import { useToast } from '@/shared/ui/toast';
import { authApi } from '@/shared/api/resources';
import { toErrorMessage } from '@/shared/lib/errors';
import { InstallButton } from '../pwa/install-button';
import { AppSidebar } from './app-sidebar';
import { LocaleSwitcher } from './locale-switcher';
import { ThemeSwitcher } from './theme-switcher';

/** The account menu: profile, password change and sign-out. */
function UserMenu() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const toast = useToast();
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  if (!user) {
    return null;
  }

  const initials = user.fullName
    .split(' ')
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t('auth.myProfile')}
          >
            <span className="bg-primary text-primary-foreground flex size-6 items-center justify-center rounded-full text-[10px] font-semibold">
              {initials || <UserRound className="size-3.5" />}
            </span>
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="min-w-56">
          <DropdownMenuLabel className="font-normal">
            <p className="truncate text-sm font-medium">{user.fullName}</p>
            <p className="text-muted-foreground truncate text-xs">
              {user.email}
            </p>
            <p className="text-muted-foreground mt-1 text-xs">
              {t('auth.signedInAs')} {t(ROLE_LABEL_KEY[user.role])}
            </p>
          </DropdownMenuLabel>

          <DropdownMenuSeparator />

          <DropdownMenuItem onSelect={() => setPasswordOpen(true)}>
            <KeyRound />
            {t('auth.changePassword')}
          </DropdownMenuItem>

          <DropdownMenuItem
            variant="destructive"
            onSelect={() => setSignOutOpen(true)}
          >
            <LogOut />
            {t('auth.signOut')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDialog
        open={signOutOpen}
        onOpenChange={setSignOutOpen}
        title={t('auth.signOutConfirm')}
        confirmLabel={t('auth.signOut')}
        onConfirm={() => {
          setSignOutOpen(false);
          void logout();
        }}
      />

      <ChangePasswordDialog
        open={passwordOpen}
        onOpenChange={setPasswordOpen}
      />
    </>
  );
}

function ChangePasswordDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [saving, setSaving] = useState(false);

  const mismatch = confirmation.length > 0 && newPassword !== confirmation;

  const reset = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmation('');
  };

  const submit = async () => {
    if (mismatch || !currentPassword || newPassword.length < 8) {
      return;
    }

    setSaving(true);
    try {
      await authApi.changePassword({ currentPassword, newPassword });
      toast.success(t('auth.passwordChanged'));
      reset();
      onOpenChange(false);
    } catch (error) {
      toast.error(t('common.error'), toErrorMessage(error, t));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('auth.changePassword')}</DialogTitle>
          <DialogDescription>{t('users.passwordHint')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <FormField
            label={t('auth.currentPassword')}
            htmlFor="current-password"
            required
          >
            <Input
              id="current-password"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
          </FormField>

          <FormField
            label={t('auth.newPassword')}
            htmlFor="new-password"
            required
          >
            <Input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          </FormField>

          <FormField
            label={t('auth.confirmPassword')}
            htmlFor="confirm-password"
            required
            error={mismatch ? t('auth.passwordMismatch') : undefined}
          >
            <Input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
            />
          </FormField>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => void submit()}
            loading={saving}
            disabled={mismatch || !currentPassword || newPassword.length < 8}
          >
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Top bar with the mobile drawer trigger and the global controls. */
export function AppTopbar() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="bg-background/95 supports-[backdrop-filter]:bg-background/80 sticky top-0 z-40 flex h-14 items-center gap-2 border-b px-3 backdrop-blur lg:px-6">
      <Button
        variant="ghost"
        size="icon-sm"
        className="lg:hidden"
        onClick={() => setDrawerOpen(true)}
        aria-label={t('common.openMenu')}
      >
        <Menu />
      </Button>

      <div className="flex min-w-0 flex-1 items-center gap-2 lg:hidden">
        <img src="/icons/icon-192.png" alt="" className="size-6 rounded-md" />
        <span className="truncate text-sm font-semibold">
          {user?.displayName || user?.tenantName || t('app.name')}
        </span>
      </div>

      <div className="hidden flex-1 lg:block" />

      <div className="flex items-center gap-1">
        {/* To the left of the language picker, as the header's reading order
            goes: install the app, then choose how it speaks, then pick a
            theme, then the account. */}
        <InstallButton />
        <LocaleSwitcher />
        <ThemeSwitcher />
        <UserMenu />
      </div>

      <Dialog open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DialogContent
          showCloseButton={false}
          className={cn(
            'top-0 left-0 h-dvh w-72 max-w-[85vw] translate-x-0 translate-y-0 rounded-none border-y-0 border-l-0 p-0',
            // `max-h-none` undoes the base surface's viewport cap, which would
            // otherwise leave a strip of the page showing under the rail.
            'max-h-none',
            // Override the centred dialog animation: this one slides from the edge.
            'data-[state=open]:animate-none data-[state=closed]:animate-none',
          )}
          aria-describedby={undefined}
        >
          <DialogTitle className="sr-only">{t('nav.more')}</DialogTitle>
          <AppSidebar onNavigate={() => setDrawerOpen(false)} />
        </DialogContent>
      </Dialog>
    </header>
  );
}

export { UserMenu };
